import backendConfig from "../config/backendConfig.ts";
import { CategoryType } from "../models/category-type.ts";
import { Category } from "../models/category.ts";

// Helper function to make API requests
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const url = `${backendConfig.API_BASE_URL}${endpoint}`;
  
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }
  
  return response.json();
};

export const getCategories = async (): Promise<Category[]> => {
  const result = await apiRequest(backendConfig.ENDPOINTS.CATEGORIES);
  if (!result.success) {
    throw new Error(result.error || 'Failed to fetch categories');
  }
  return result.data.map((category: any) => ({
    ...category,
    id: category._id,
    createdAt: new Date(category.createdAt),
    updatedAt: new Date(category.updatedAt),
  }));
};

export const addCategory = async (name: string): Promise<Category | string> => {
  const trimmed = name.trim();
  if (!trimmed) return "Category name cannot be empty.";

  try {
    const result = await apiRequest(backendConfig.ENDPOINTS.CATEGORIES, {
      method: 'POST',
      body: JSON.stringify({ name: trimmed }),
    });

    if (!result.success) {
      return result.error || "Failed to create category";
    }

    return {
      id: result.data._id,
      name: result.data.name as CategoryType,
      createdAt: new Date(result.data.createdAt),
      updatedAt: new Date(result.data.updatedAt),
      quantity: result.data.quantity || 0,
    };
  } catch (error: any) {
    return error.message;
  }
};

export const updateCategory = async (
  id: string,
  name: string
): Promise<Category | string> => {
  const trimmed = name.trim();
  if (!trimmed) return "Category name cannot be empty.";

  try {
    const result = await apiRequest(`${backendConfig.ENDPOINTS.CATEGORIES}/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ name: trimmed }),
    });

    if (!result.success) {
      return result.error || "Failed to update category";
    }

    return {
      id: result.data._id,
      name: result.data.name as CategoryType,
      createdAt: new Date(result.data.createdAt),
      updatedAt: new Date(result.data.updatedAt),
      quantity: result.data.quantity || 0,
    };
  } catch (error: any) {
    return error.message;
  }
};

export const deleteCategory = async (id: string): Promise<void> => {
  const result = await apiRequest(`${backendConfig.ENDPOINTS.CATEGORIES}/${id}`, {
    method: 'DELETE',
  });
  
  if (!result.success) {
    throw new Error(result.error || 'Failed to delete category');
  }
};

export const getCategoryProductCount = async (
  categoryId: string
): Promise<{ success: boolean; count?: number; error?: unknown }> => {
  try {
    const result = await apiRequest(`${backendConfig.ENDPOINTS.CATEGORIES}/${categoryId}/product-count`);
    
    if (!result.success) {
      return { success: false, error: result.error };
    }
    
    return { success: true, count: result.count };
  } catch (error) {
    console.error("Getting category product count failed:", error);
    return { success: false, error };
  }
};

export const incrementCategoryQuantity = async (
  categoryId: string,
  amount: number
) => {
  const result = await apiRequest(`${backendConfig.ENDPOINTS.CATEGORIES}/${categoryId}/increment`, {
    method: 'PATCH',
    body: JSON.stringify({ amount }),
  });
  
  if (!result.success) {
    throw new Error(result.error || 'Failed to increment category quantity');
  }
};

export const getCategoriesWithQuantities = async (): Promise<{
  success: boolean;
  data?: Category[];
  error?: unknown;
}> => {
  try {
    const categories = await getCategories();
    return { success: true, data: categories };
  } catch (error) {
    console.error("Fetching categories failed:", error);
    return { success: false, error };
  }
};
